import * as v from "valibot";
import { IPlantDBModel } from "../plant";
import type { AppPermissionIdentifierWithoutWildcard } from "#interface/auth";
import { DEFAULT_AVG_WORK_TIME_SECONDS } from "@dmca/utils/work-time";

export enum CellFeaturesEnum {
  CALL_HELP = "call_help",
  JOB_STORAGE = "job_storage",
  JOB_NOTIFICATION = "job_notification",
  KANBAN = "kanban",
  MACHINE_MONITORING = "machine_monitoring",
  PULSE_METER = "pulse_meter",
  RFID = "rfid",
  WELD = "weld",
}

export const CellFeaturesNameMap = {
  [CellFeaturesEnum.CALL_HELP]: "Call Help",
  [CellFeaturesEnum.JOB_STORAGE]: "Job Storage",
  [CellFeaturesEnum.JOB_NOTIFICATION]: "Job Notification",
  [CellFeaturesEnum.KANBAN]: "Kanban",
  [CellFeaturesEnum.MACHINE_MONITORING]: "Machine Monitoring",
  [CellFeaturesEnum.PULSE_METER]: "Pulse Meter",
  [CellFeaturesEnum.RFID]: "RFID",
  [CellFeaturesEnum.WELD]: "Welding",
} satisfies { [key in CellFeaturesEnum]: string };

export const CellFeaturesPermissionsMap = {
  [CellFeaturesEnum.CALL_HELP]: "CallHelp::ViewFeature",
  [CellFeaturesEnum.JOB_STORAGE]: "ProductionJob::ViewJobStorage",
  [CellFeaturesEnum.JOB_NOTIFICATION]: "Admin::UpdateNotification",
  [CellFeaturesEnum.KANBAN]: "Kanban::ViewFeature",
  [CellFeaturesEnum.MACHINE_MONITORING]: "MachineMonitoring::ViewFeature",
  [CellFeaturesEnum.PULSE_METER]: "PulseMeter::ViewFeature",
  [CellFeaturesEnum.RFID]: "RFID::ViewFeature",
  [CellFeaturesEnum.WELD]: "WeldingParams::ViewWeldingParams",
} satisfies {
  [key in CellFeaturesEnum]: AppPermissionIdentifierWithoutWildcard;
};

export const ICellLocationDBModel = v.object({
  id: v.string(),
  isRFIDAutoClose: v.boolean(),
  informsProjectStorageStatus: v.optional(v.boolean()),
  kanbanStatusUpdate: v.nullable(v.picklist(["in-progress", "completed"])),
  locationName: v.string(),
  plantId: v.string(),
  mapX: v.nullable(v.number()),
  mapY: v.nullable(v.number()),
  bayNumber: v.optional(v.nullable(v.number())),
  columnNumber: v.optional(v.nullable(v.number())),
  avgWorkTimeSeconds: v.optional(v.number()),
  featureFlag: v.array(v.enum(CellFeaturesEnum)),
});
export type ICellLocationDBModel = v.InferOutput<typeof ICellLocationDBModel>;

export const ICellLocationDBModelWithRelations = v.object({
  ["cell_location"]: ICellLocationDBModel,
  ["plant"]: IPlantDBModel,
});
export type ICellLocationDBModelWithRelations = v.InferOutput<
  typeof ICellLocationDBModelWithRelations
>;

export const ICellLocation = v.object({
  cellId: v.string(),
  plantId: v.string(),
  isRFIDAutoClose: v.boolean(),
  informsProjectStorageStatus: v.optional(v.boolean()),
  kanbanStatusUpdate: v.optional(v.picklist(["in-progress", "completed"])),
  featureFlag: v.array(v.enum(CellFeaturesEnum)),
  locationName: v.string(),
  plantName: v.string(),
  mapX: v.nullable(v.number()),
  mapY: v.nullable(v.number()),
  bayNumber: v.optional(v.nullable(v.number())),
  columnNumber: v.optional(v.nullable(v.number())),
  avgWorkTimeSeconds: v.optional(v.number()),
});
export type ICellLocation = v.InferOutput<typeof ICellLocation>;

export const ICellLocationTransform = v.pipe(
  ICellLocationDBModelWithRelations,
  v.transform((data) => {
    const cellTableName = "cell_location";
    const plantTableName = "plant";
    return {
      cellId: data[cellTableName].id,
      plantId: data[plantTableName].id,
      isRFIDAutoClose: data[cellTableName].isRFIDAutoClose,
      informsProjectStorageStatus:
        data[cellTableName].informsProjectStorageStatus ?? false,
      kanbanStatusUpdate: data[cellTableName].kanbanStatusUpdate || undefined,
      featureFlag: data[cellTableName].featureFlag,
      locationName: data[cellTableName].locationName,
      plantName: data[plantTableName].name,
      mapX: data[cellTableName].mapX,
      mapY: data[cellTableName].mapY,
      bayNumber: data[cellTableName].bayNumber,
      columnNumber: data[cellTableName].columnNumber,
      avgWorkTimeSeconds:
        data[cellTableName].avgWorkTimeSeconds ?? DEFAULT_AVG_WORK_TIME_SECONDS,
    } satisfies ICellLocation;
  })
);
